/**
 * @file React hook — useDatasetOverviewSections.ts: form/state logic for contributor dashboard.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { DATASET_OVERVIEW_SECTION_KEYS } from "./types";

/** Hook: use Dataset Overview Sections. */
export function useDatasetOverviewSections() {
  const sectionKeys = useMemo(
    () => [...DATASET_OVERVIEW_SECTION_KEYS],
    []
  );

  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >(() => Object.fromEntries(sectionKeys.map((k) => [k, true])));

  useEffect(() => {
    setExpandedSections((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const k of sectionKeys) {
        if (!(k in next)) {
          next[k] = true;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [sectionKeys]);

  const setSectionExpanded = useCallback((key: string, open: boolean) => {
    setExpandedSections((prev) => ({ ...prev, [key]: open }));
  }, []);

  const expandAll = useCallback(() => {
    setExpandedSections(
      Object.fromEntries(sectionKeys.map((k) => [k, true]))
    );
  }, [sectionKeys]);

  const collapseAll = useCallback(() => {
    setExpandedSections(
      Object.fromEntries(sectionKeys.map((k) => [k, false]))
    );
  }, [sectionKeys]);

  const isExpanded = useCallback(
    (key: string) => expandedSections[key] !== false,
    [expandedSections]
  );

  return {
    sectionKeys,
    isExpanded,
    setSectionExpanded,
    expandAll,
    collapseAll,
  };
}
